import { useState, useEffect, useCallback, useRef } from "react";
import EventApp from "./EventApp.jsx";
import { listEvents, createEvent, renameEvent, deleteEvent, loadEvent } from "./lib/store.js";
import { useTheme } from "./lib/settings.js";
import { useSession, isUserAllowed, userEmail } from "./lib/auth.js";
import { I } from "./components/Icons.jsx";
import Btn from "./components/ui/Btn.jsx";
import Display from "./components/ui/Display.jsx";
import Eyebrow from "./components/ui/Eyebrow.jsx";
import Ornament from "./components/ui/Ornament.jsx";
import SettingsPopover from "./components/SettingsPopover.jsx";
import SignIn from "./components/SignIn.jsx";
import UserMenu from "./components/UserMenu.jsx";

const STORED_EVENT_KEY = "placement:event";

export default function App() {
  const [theme, setTheme] = useTheme();
  const { loading: authLoading, session } = useSession();
  const [allowed, setAllowed] = useState(null); // null = unknown, bool = resolved
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem(STORED_EVENT_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [navMode, setNavMode] = useState(() => {
    try { return localStorage.getItem("placement:nav") || "sidebar"; } catch { return "sidebar"; }
  });
  const [poolPosition, setPoolPosition] = useState(() => {
    try { return localStorage.getItem("placement:pool") || "side"; } catch { return "side"; }
  });
  const settingsBtnRef = useRef(null);

  useEffect(() => { try { localStorage.setItem("placement:nav", navMode); } catch {} }, [navMode]);
  useEffect(() => { try { localStorage.setItem("placement:pool", poolPosition); } catch {} }, [poolPosition]);

  useEffect(() => {
    try {
      if (activeId) localStorage.setItem(STORED_EVENT_KEY, activeId);
      else localStorage.removeItem(STORED_EVENT_KEY);
    } catch {}
  }, [activeId]);

  // Whenever a session appears, check the allowlist. A "false" keeps the
  // session so the denied screen can show the email; the user signs out
  // explicitly from there. RLS still blocks every data call in the meantime.
  useEffect(() => {
    let cancelled = false;
    if (!session) { setAllowed(null); return; }
    setAllowed(null);
    (async () => {
      const ok = await isUserAllowed();
      if (!cancelled) setAllowed(ok);
    })();
    return () => { cancelled = true; };
  }, [session]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const list = await listEvents();
      setEvents(list);
      // Load per-event counts in the background.
      const results = await Promise.allSettled(list.map(e => loadEvent(e.id)));
      const next = {};
      list.forEach((e, i) => {
        const r = results[i];
        if (r.status === "fulfilled") {
          next[e.id] = {
            guests: r.value.people.length,
            activities: r.value.activities.length,
          };
        }
      });
      setStats(next);
      return list;
    } catch (e) {
      setError(e.message || String(e));
      return [];
    }
  }, []);

  useEffect(() => {
    if (allowed !== true) return;
    setLoading(true);
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh, allowed]);

  const handleCreate = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const ev = await createEvent(newName.trim());
      await refresh();
      setActiveId(ev.id);
      setCreating(false);
      setNewName("");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete event "${name}" and all its data? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await deleteEvent(id);
      await refresh();
      if (activeId === id) setActiveId(null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (id, currentName) => {
    const raw = prompt("Rename event:", currentName);
    if (raw === null) return;
    const next = raw.trim();
    if (!next || next === currentName) return;
    try {
      await renameEvent(id, next);
      await refresh();
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  // Auth gate. Four possible states:
  //   1. auth still loading → tiny splash
  //   2. no session → SignIn
  //   3. session but allowlist check pending → tiny splash
  //   4. session but not on allowlist → SignIn in "denied" mode
  if (authLoading) {
    return <AuthSplash />;
  }
  if (!session) {
    return <SignIn />;
  }
  if (allowed === null) {
    return <AuthSplash />;
  }
  if (allowed === false) {
    return <SignIn notAllowedEmail={userEmail(session)} session={session} />;
  }

  if (activeId) {
    const ev = events.find(e => e.id === activeId);
    return (
      <EventApp
        eventId={activeId}
        eventName={ev?.name || "Event"}
        onBack={() => setActiveId(null)}
        theme={theme}
        setTheme={setTheme}
        navMode={navMode}
        setNavMode={setNavMode}
        poolPosition={poolPosition}
        setPoolPosition={setPoolPosition}
        session={session}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper-0)", color: "var(--ink)", fontFamily: "var(--ui)", position: "relative" }}>
      {/* Top brand bar */}
      <div style={{
        padding: "22px 48px",
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid var(--line)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/atrum-logo-white.png" className="brand-logo" alt="Atrum" style={{ width: 26, height: 28, objectFit: "contain" }} />
          <div style={{ width: 1, height: 22, background: "var(--line-2)" }} />
          <div>
            <div style={{ fontFamily: "var(--display)", fontSize: 17, fontStyle: "italic", letterSpacing: 0.2, lineHeight: 1 }}>
              Placement
            </div>
            <div style={{ fontSize: 10.5, color: "var(--ink-3)", letterSpacing: 1.6, textTransform: "uppercase", fontWeight: 600, marginTop: 3 }}>
              by Atrum
            </div>
          </div>
          <div style={{ width: 1, height: 22, background: "var(--line-2)", margin: "0 6px" }} />
          <div style={{ fontSize: 12, color: "var(--ink-3)", letterSpacing: 0.3 }}>
            Seating &amp; groups for our gatherings
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 12, color: "var(--ink-3)" }}>
          <button
            ref={settingsBtnRef}
            type="button"
            onClick={() => setSettingsOpen(v => !v)}
            style={{
              background: "none",
              border: "none",
              color: "var(--ink-3)",
              cursor: "pointer",
              fontFamily: "var(--ui)",
              fontSize: 12,
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <I.settings size={13} stroke="currentColor" /> Settings
          </button>
          <UserMenu session={session} />
        </div>
      </div>

      {settingsOpen && (
        <SettingsPopover
          anchor={settingsBtnRef.current}
          theme={theme}
          setTheme={setTheme}
          navMode={navMode}
          setNavMode={setNavMode}
          poolPosition={poolPosition}
          setPoolPosition={setPoolPosition}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      <div style={{ maxWidth: 980, margin: "0 auto", padding: "64px 48px 80px" }}>
        {/* Hero */}
        <Display size={56} style={{ maxWidth: 720, marginTop: 8, marginBottom: 16 }}>
          Who sits next to whom,<br />
          <span style={{ fontStyle: "italic", color: "var(--ink-2)" }}>deserves thought.</span>
        </Display>
        <div style={{ color: "var(--ink-2)", fontSize: 15, lineHeight: 1.55, maxWidth: 560, marginBottom: 44 }}>
          Choose an event to continue planning, or begin a new one. Import your guest list, shape activities, assemble tables, salons and vehicles.
        </div>

        <Ornament style={{ marginBottom: 28 }} />

        {error && (
          <div style={{
            padding: "12px 16px",
            borderRadius: 10,
            background: "var(--warn-soft)",
            border: "1px solid var(--warn-line)",
            color: "var(--warn-ink)",
            fontSize: 13,
            marginBottom: 20,
          }}>{error}</div>
        )}

        {loading ? (
          <div style={{ color: "var(--ink-3)", fontStyle: "italic" }}>Loading…</div>
        ) : (
          <>
            {/* Events table */}
            <div style={{ marginBottom: 28 }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 110px 70px 80px 240px",
                padding: "10px 4px",
                borderBottom: "1px solid var(--line)",
                fontSize: 11,
                color: "var(--ink-3)",
                letterSpacing: 1.5,
                textTransform: "uppercase",
                fontWeight: 600,
              }}>
                <div>Event</div>
                <div>Created</div>
                <div style={{ textAlign: "right" }}>Guests</div>
                <div style={{ textAlign: "right" }}>Activities</div>
                <div />
              </div>

              {events.length === 0 && (
                <div style={{ padding: "30px 4px", color: "var(--ink-3)", fontStyle: "italic", fontSize: 13 }}>
                  No events yet. Begin a new one below.
                </div>
              )}

              {events.map(ev => {
                const s = stats[ev.id] || { guests: "–", activities: "–" };
                return (
                  <div
                    key={ev.id}
                    onClick={() => setActiveId(ev.id)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-1)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 110px 70px 80px 240px",
                      alignItems: "center",
                      padding: "22px 4px",
                      borderBottom: "1px solid var(--line)",
                      cursor: "pointer",
                      transition: "background 0.12s",
                    }}
                  >
                    <div>
                      <div style={{
                        fontFamily: "var(--display)",
                        fontSize: 22,
                        fontWeight: 400,
                        color: "var(--ink)",
                        marginBottom: 4,
                        letterSpacing: -0.1,
                      }}>{ev.name}</div>
                      <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                        Created {new Date(ev.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                    </div>
                    <div style={{ fontSize: 12.5, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" }}>
                      {new Date(ev.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                    <div style={{ textAlign: "right", fontSize: 14, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
                      {s.guests}
                    </div>
                    <div style={{ textAlign: "right", fontSize: 14, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
                      {s.activities}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }} onClick={e => e.stopPropagation()}>
                      <Btn variant="quiet" size="sm" onClick={() => handleRename(ev.id, ev.name)}>Rename</Btn>
                      <Btn variant="danger" size="sm" onClick={() => handleDelete(ev.id, ev.name)}>Delete</Btn>
                      <Btn variant="ghost" size="sm" onClick={() => setActiveId(ev.id)}>
                        Open <I.chevronRight size={13} stroke="currentColor" />
                      </Btn>
                    </div>
                  </div>
                );
              })}
            </div>

            {!creating ? (
              <Btn variant="dashed" icon={I.plus} size="md" onClick={() => setCreating(true)}>
                Begin a new event
              </Btn>
            ) : (
              <div style={{ padding: 22, border: "1px solid var(--line)", borderRadius: 12, background: "var(--paper-1)" }}>
                <Eyebrow style={{ marginBottom: 10 }}>New event</Eyebrow>
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Meridian Autumn Gathering"
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); if (e.key === "Escape") { setCreating(false); setNewName(""); } }}
                  style={{
                    width: "100%",
                    padding: "11px 14px",
                    borderRadius: 8,
                    border: "1px solid var(--line-2)",
                    background: "var(--paper-0)",
                    color: "var(--ink)",
                    fontSize: 15,
                    fontFamily: "var(--ui)",
                    boxSizing: "border-box",
                    marginBottom: 14,
                    outline: "none",
                  }}
                />
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 16 }}>
                  You'll import your guest list and create activities in the next step.
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn variant="primary" size="md" disabled={!newName.trim() || busy} onClick={handleCreate}>
                    {busy ? "Creating…" : "Create event"}
                  </Btn>
                  <Btn variant="quiet" size="md" onClick={() => { setCreating(false); setNewName(""); }}>
                    Cancel
                  </Btn>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AuthSplash() {
  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--paper-0)",
      color: "var(--ink-3)",
      fontFamily: "var(--ui)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontStyle: "italic",
      fontSize: 13,
    }}>
      Loading…
    </div>
  );
}
